interface SkeletonProps {
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  className?: string;
}

export function Skeleton({
  variant = 'rectangular',
  width,
  height,
  className = '',
}: SkeletonProps) {
  const baseClasses = 'animate-shimmer';

  const variantClasses = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      style={style}
    />
  );
}

// Pre-composed skeleton patterns for common use cases
export function SkeletonTrackRow() {
  return (
    <div className="flex items-center gap-3 p-3">
      <Skeleton variant="text" width={24} height={24} />
      <Skeleton variant="rectangular" width={6} height={32} />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" height={16} className="w-3/4" />
        <Skeleton variant="text" height={12} className="w-1/2" />
      </div>
      <Skeleton variant="text" width={32} height={12} />
    </div>
  );
}

export function SkeletonPhaseGroup({ trackCount = 3 }: { trackCount?: number }) {
  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Skeleton variant="circular" width={12} height={12} />
          <Skeleton variant="text" width={80} height={16} />
        </div>
        <Skeleton variant="text" width={100} height={14} />
      </div>
      <div className="space-y-2">
        {Array.from({ length: trackCount }).map((_, i) => (
          <SkeletonTrackRow key={i} />
        ))}
      </div>
    </div>
  );
}

export function SkeletonNowPlaying() {
  return (
    <div className="glass rounded-2xl p-6">
      {/* Album Art */}
      <div className="w-32 h-32 mx-auto mb-4">
        <Skeleton variant="rectangular" className="w-full h-full rounded-xl" />
      </div>

      {/* Phase Indicator */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <Skeleton variant="circular" width={8} height={8} />
        <Skeleton variant="text" width={60} height={14} />
      </div>

      {/* Track Info */}
      <div className="text-center mb-6 space-y-2">
        <Skeleton variant="text" height={24} className="w-2/3 mx-auto" />
        <Skeleton variant="text" height={16} className="w-1/2 mx-auto" />
      </div>

      {/* Playback Controls */}
      <div className="flex items-center justify-center gap-6">
        <Skeleton variant="circular" width={44} height={44} />
        <Skeleton variant="circular" width={64} height={64} />
        <Skeleton variant="circular" width={44} height={44} />
      </div>
    </div>
  );
}
